import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import SubmissionAppService from '../../../../application/platform/ser/submission/submission-app-service';
import { useFeedback } from '../../../../hooks/use-feedback';
import { CONTENT_ORIGIN_LABEL, ContentOrigin } from '../../../../model/ser/ser-enums';
import { ISubmissionFileContentResponse } from '../../../../model/ser/submission/response/submission-file-content-response';
import { formatDateTime } from '../../../../utils/date';

interface FileContentDialogProps {
    submissionId: number;
    document?: { id: number; name?: string };
    onClose: () => void;
}

// Texto extraído (nativo ou OCR) de um documento da remessa
export function FileContentDialog({ submissionId, document, onClose }: FileContentDialogProps) {
    const { showError } = useFeedback();
    const [content, setContent] = useState<ISubmissionFileContentResponse>();
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!document) return;
        setContent(undefined);
        setCopied(false);
        setLoading(true);
        SubmissionAppService.getFileContent(submissionId, document.id)
            .then(setContent)
            .catch((error) => {
                showError('Não foi possível carregar o conteúdo do documento', error);
                onClose();
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submissionId, document?.id]);

    async function copy() {
        try {
            await navigator.clipboard.writeText(content?.content ?? '');
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error: any) {
            showError('Não foi possível copiar o texto', error);
        }
    }

    const meta = [
        content?.origin ? CONTENT_ORIGIN_LABEL[content.origin as ContentOrigin] : undefined,
        content?.averageConfidence !== undefined && content?.averageConfidence !== null ? `Confiança média ${content.averageConfidence.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` : undefined,
        content?.characterCount !== undefined && content?.characterCount !== null ? `${content.characterCount.toLocaleString('pt-BR')} caracteres` : undefined,
        content?.extractedAt ? `Extraído em ${formatDateTime(content.extractedAt)}` : undefined,
    ].filter(Boolean) as string[];

    return (
        <Dialog open={!!document} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" component="div">Conteúdo extraído</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">{document?.name}</Typography>
                </Box>
                <IconButton onClick={onClose} aria-label="Fechar" sx={{ position: 'absolute', right: 12, top: 12 }}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading && <LinearProgress />}
                {meta.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {meta.map((item) => <Chip key={item} size="small" label={item} />)}
                    </Box>
                )}
                {content?.extractionError && <Alert severity="error" sx={{ mb: 2 }}>{content.extractionError}</Alert>}
                {content && (
                    content.content
                        ? (
                            <Box
                                component="pre"
                                sx={{
                                    m: 0, p: 2, maxHeight: '55vh', overflow: 'auto', borderRadius: 1,
                                    bgcolor: 'action.hover', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                    fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                }}
                            >
                                {content.content}
                            </Box>
                        )
                        : <Typography color="text.secondary">Nenhum texto foi extraído deste documento.</Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
                <Button onClick={onClose}>Fechar</Button>
                <Button variant="contained" startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />} onClick={copy} disabled={!content?.content}>
                    {copied ? 'Copiado!' : 'Copiar texto'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
